import { useState, useEffect, useCallback, useRef } from 'react';
import { categoriesService } from '../lib/database';
import { liveDataStatus } from '../lib/liveDataStatus';
import { getDataSource } from '../lib/config';
import { categories as initialCategories } from '../data/menu';
import { menuCategoryChangeSummaries } from '../lib/auditSummaries';
import { writeClientAudit } from '../lib/clientAudit';
import type { Category } from '../types';

interface UseCategoriesReturn {
  categories: Category[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (category: Omit<Category, 'id'>) => Promise<Category>;
  update: (id: number | string, updates: Partial<Category>) => Promise<Category>;
  remove: (id: number | string) => Promise<void>;
}

export const useCategories = (): UseCategoriesReturn => {
  const [categories, setCategories] = useState<Category[]>(initialCategories);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track the most recent unsubscribe so `refresh()` can re-subscribe cleanly.
  const unsubRef = useRef<(() => void) | null>(null);
  // First snapshot flips loading -> false. Without this we'd report "loading" forever in mock mode.
  const gotFirstSnapshotRef = useRef(false);

  const subscribe = useCallback(() => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      setCategories(initialCategories);
      setLoading(false);
      gotFirstSnapshotRef.current = true;
      return () => {};
    }

    setLoading(!gotFirstSnapshotRef.current);
    setError(null);
    console.log('🔄 useCategories subscribing to Firestore...');

    return categoriesService.subscribe((data) => {
      gotFirstSnapshotRef.current = true;
      setCategories(data);
      setLoading(false);
      setError(null);
    });
  }, []);

  useEffect(() => {
    unsubRef.current = subscribe();
    return () => {
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
    };
  }, [subscribe]);

  // Manual refresh - kept for API compatibility. The reconnector handles
  // most cases automatically, but callers (e.g. App.tsx visibility handler)
  // can still force an immediate re-subscribe.
  const refresh = useCallback(async (): Promise<void> => {
    // Find the categories feed in the registry and ask it to refresh.
    // If it's not registered (e.g. mock mode) this is a no-op.
    const feeds = liveDataStatus.getAll();
    if (feeds.some(f => f.name === 'categories')) {
      // The registry exposes refreshAll(); for a single feed we just trigger
      // an unsubscribe + resubscribe via the local hook.
      if (unsubRef.current) {
        unsubRef.current();
        unsubRef.current = null;
      }
      unsubRef.current = subscribe();
    }
  }, [subscribe]);

  const create = useCallback(async (category: Omit<Category, 'id'>): Promise<Category> => {
    const dataSource = getDataSource();
    if (dataSource === 'mock') {
      const numericIds = categories.map(c => (typeof c.id === 'number' ? c.id : 0));
      const newCategory: Category = {
        ...category,
        id: (numericIds.length ? Math.max(...numericIds, 0) : 0) + 1,
      };
      setCategories(prev => [...prev, newCategory]);
      return newCategory;
    }

    setError(null);
    try {
      const newCategory = await categoriesService.create(category);
      setCategories(prev => prev.some(c => String(c.id) === String(newCategory.id)) ? prev : [...prev, newCategory]);
      writeClientAudit({
        action: 'menu.category_changed',
        entityType: 'menu',
        entityId: `category-${newCategory.id}`,
        summary: `Category added · ${newCategory.name}`,
      });
      return newCategory;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create category';
      setError(errorMessage);
      throw err;
    }
  }, [categories]);

  const update = useCallback(async (id: number | string, updates: Partial<Category>): Promise<Category> => {
    const dataSource = getDataSource();
    const idNorm = String(id);
    if (dataSource === 'mock') {
      const updatedCategory = categories.find(c => String(c.id) === idNorm);
      if (!updatedCategory) throw new Error('Category not found');
      const newCategory = { ...updatedCategory, ...updates };
      setCategories(prev => prev.map(c => String(c.id) === idNorm ? newCategory : c));
      return newCategory;
    }

    setError(null);
    try {
      const previous = categories.find(c => String(c.id) === idNorm);
      const updatedCategory = await categoriesService.update(id, updates);
      setCategories(prev => prev.map(c => String(c.id) === idNorm ? updatedCategory : c));
      if (previous) {
        const lines = menuCategoryChangeSummaries(String(previous.name || 'Category'), previous, updates);
        if (lines.length) {
          writeClientAudit({
            action: 'menu.category_changed',
            entityType: 'menu',
            entityId: `category-${id}`,
            summary: lines.join(' · '),
          });
        }
      }
      return updatedCategory;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update category';
      setError(errorMessage);
      throw err;
    }
  }, [categories]);

  const remove = useCallback(async (id: number | string): Promise<void> => {
    const dataSource = getDataSource();
    const idNorm = String(id);
    if (dataSource === 'mock') {
      setCategories(prev => prev.filter(c => String(c.id) !== idNorm));
      return;
    }

    setError(null);
    try {
      const previous = categories.find(c => String(c.id) === idNorm);
      await categoriesService.delete(id);
      setCategories(prev => prev.filter(c => String(c.id) !== idNorm));
      writeClientAudit({
        action: 'menu.category_changed',
        entityType: 'menu',
        entityId: `category-${id}`,
        summary: `Category removed · ${previous?.name || 'Category'}`,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete category';
      setError(errorMessage);
      throw err;
    }
  }, [categories]);

  return {
    categories,
    loading,
    error,
    refresh,
    create,
    update,
    remove,
  };
};
